/**
 * Sentry init — opt-in via VITE_SENTRY_DSN.
 *
 * DSN vazio = no-op completo (zero overhead, sem rede, sem ErrorBoundary
 * acionando captura). Ativa só em prod por default (`environment` reflete
 * `import.meta.env.PROD`).
 *
 * Privacy crítico: `beforeSend` redacta valores sensíveis ANTES do POST pro
 * Sentry — passwords, tokens, cookies, e principalmente IPs internos da
 * malha UNE (`100.89.255.*` é Tailscale UNE — vazar = expor topologia).
 *
 * Sampling:
 * - tracesSampleRate 0.1 → 10% das transações (perf monitoring)
 * - replaysSessionSampleRate 0 → ZERO replay em sessão normal
 * - replaysOnErrorSampleRate 0.5 → 50% replay quando há erro (debug útil
 *   sem custo nem privacy leak em sessão saudável)
 */

import * as Sentry from '@sentry/react';
import { env } from './env';

const REDACT_KEYS = /password|token|authorization|cookie|community|secret|api[-_]?key/i;
const IP_INTERNAL = /\b100\.89\.255\.\d{1,3}\b/g;

function redact(input: unknown): unknown {
  if (typeof input === 'string') {
    return input.replace(IP_INTERNAL, '<REDACTED-IP>');
  }
  if (Array.isArray(input)) {
    return input.map(redact);
  }
  if (input && typeof input === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      out[k] = REDACT_KEYS.test(k) ? '<REDACTED>' : redact(v);
    }
    return out;
  }
  return input;
}

function beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  return redact(event) as Sentry.ErrorEvent;
}

let initialized = false;

export function initSentry(): void {
  if (initialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (typeof dsn !== 'string' || dsn.length === 0) {
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.PROD ? 'production' : 'development',
    release: env.BUILD_VERSION,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.5,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    beforeSend,
  });

  initialized = true;
}
